import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Stage, Layer, Rect, Circle, Text, Group, Line } from 'react-konva';
import Konva from 'konva';
import { StallGeometry, FacilityZone, PillarMarker, FLOORPLAN_DIMENSIONS } from '../../data/floorplanData';
import { Booth } from '../../lib/api';

export interface KonvaCanvasProps {
  stalls: StallGeometry[];
  facilities: FacilityZone[];
  pillars: PillarMarker[];
  dimensions?: typeof FLOORPLAN_DIMENSIONS;
  selectedElement: { type: 'stall' | 'facility' | 'pillar'; id: string } | null;
  onSelectElement: (type: 'stall' | 'facility' | 'pillar', id: string) => void;
  onUpdateStallPosition: (id: string, x: number, y: number) => boolean;
  onUpdateFacilityPosition: (id: string, x: number, y: number) => boolean;
  onDropFromLibrary: (presetData: any, x: number, y: number) => void;
  onOverlapRejection?: (draggedId: string, collidedWithId: string) => void;
  boothsMap?: Record<string, Booth>;
  zoomScale: number;
  snapGridSize: number;
  showGrid: boolean;
  preventOverlap: boolean;
  containerClassName?: string;
}

// Axis-Aligned Bounding Box (AABB) overlap check
export function checkBoundingBoxOverlap(
  r1: { x: number; y: number; w: number; h: number },
  r2: { x: number; y: number; w: number; h: number },
  padding = 0.5
): boolean {
  return (
    r1.x + padding < r2.x + r2.w &&
    r1.x + r1.w - padding > r2.x &&
    r1.y + padding < r2.y + r2.h &&
    r1.y + r1.h - padding > r2.y
  );
}

export function KonvaCanvas({
  stalls,
  facilities,
  pillars,
  dimensions = FLOORPLAN_DIMENSIONS,
  selectedElement,
  onSelectElement,
  onUpdateStallPosition,
  onUpdateFacilityPosition,
  onDropFromLibrary,
  onOverlapRejection,
  boothsMap = {},
  zoomScale,
  snapGridSize,
  showGrid,
  preventOverlap,
  containerClassName,
}: KonvaCanvasProps) {
  const stageRef = useRef<Konva.Stage>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track initial position at drag start to revert if overlap is rejected
  const dragStartPositions = useRef<Record<string, { x: number; y: number }>>({});
  // Track currently colliding ID for real-time visual highlight
  const [collidingId, setCollidingId] = useState<string | null>(null);

  const hallBounds = dimensions.hallBounds || { x: 36, y: 160, w: 652, h: 715 };
  const stageWidth = dimensions.width || 724;
  const stageHeight = dimensions.height || 920;

  // Check collision for a candidate position against all other stalls
  const checkCollisionWithOtherStalls = useCallback(
    (id: string, candidate: { x: number; y: number; w: number; h: number }): string | null => {
      for (const s of stalls) {
        if (s.id === id) continue;
        if (checkBoundingBoxOverlap(candidate, { x: s.x, y: s.y, w: s.w, h: s.h })) {
          return s.id;
        }
      }
      return null;
    },
    [stalls]
  );

  // Drag over handler for HTML5 drag-and-drop from sidebar
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // Drop handler for dropping component presets from sidebar onto canvas
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const dataStr = e.dataTransfer.getData('application/json');
    if (!dataStr) return;

    try {
      const presetData = JSON.parse(dataStr);
      const stage = stageRef.current;
      if (!stage) return;

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Pointer position relative to stage
      const pointerX = e.clientX - rect.left;
      const pointerY = e.clientY - rect.top;

      // Scale correction
      const unscaledX = pointerX / zoomScale;
      const unscaledY = pointerY / zoomScale;

      // Snap to grid
      const w = presetData.w || 29;
      const h = presetData.h || 29;

      let snappedX = Math.round(unscaledX / snapGridSize) * snapGridSize;
      let snappedY = Math.round(unscaledY / snapGridSize) * snapGridSize;

      // Clamp inside hall bounds
      snappedX = Math.max(hallBounds.x, Math.min(hallBounds.x + hallBounds.w - w, snappedX));
      snappedY = Math.max(hallBounds.y, Math.min(hallBounds.y + hallBounds.h - h, snappedY));

      onDropFromLibrary(presetData, snappedX, snappedY);
    } catch (err) {
      console.error('Failed to parse dropped preset:', err);
    }
  };

  // Generate grid lines for visual guidance
  const gridLines = React.useMemo(() => {
    if (!showGrid) return [];
    const lines = [];
    const step = snapGridSize * 2; // e.g., 20px or 10px

    // Vertical lines
    for (let x = hallBounds.x; x <= hallBounds.x + hallBounds.w; x += step) {
      const isMajor = (x - hallBounds.x) % (step * 5) === 0;
      lines.push({
        points: [x, hallBounds.y, x, hallBounds.y + hallBounds.h],
        stroke: isMajor ? 'rgba(10, 61, 39, 0.18)' : 'rgba(10, 61, 39, 0.06)',
        strokeWidth: isMajor ? 0.8 : 0.4,
      });
    }

    // Horizontal lines
    for (let y = hallBounds.y; y <= hallBounds.y + hallBounds.h; y += step) {
      const isMajor = (y - hallBounds.y) % (step * 5) === 0;
      lines.push({
        points: [hallBounds.x, y, hallBounds.x + hallBounds.w, y],
        stroke: isMajor ? 'rgba(10, 61, 39, 0.18)' : 'rgba(10, 61, 39, 0.06)',
        strokeWidth: isMajor ? 0.8 : 0.4,
      });
    }

    return lines;
  }, [showGrid, snapGridSize, hallBounds]);

  return (
    <div
      ref={containerRef}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`relative flex justify-center bg-[#E2E8F0] p-4 overflow-auto scrollbar-thin select-none ${
        containerClassName || 'rounded-2xl border border-slate-300/80 min-h-[650px]'
      }`}
    >
      <div
        className="transition-transform duration-150 ease-out origin-top shrink-0 shadow-md bg-white rounded-xl"
        style={{
          width: stageWidth * zoomScale,
          height: stageHeight * zoomScale,
        }}
      >
        <Stage
          ref={stageRef}
          width={stageWidth * zoomScale}
          height={stageHeight * zoomScale}
          scaleX={zoomScale}
          scaleY={zoomScale}
          onMouseDown={(e) => {
            // Deselect if clicked on empty stage or hall background
            if (e.target === e.target.getStage() || e.target.name() === 'hallBg') {
              onSelectElement('stall', '');
            }
          }}
        >
          {/* LAYER 1: Hall Outline & Grid */}
          <Layer>
            {/* Outer Hall Boundary */}
            <Rect
              name="hallBg"
              x={hallBounds.x}
              y={hallBounds.y}
              width={hallBounds.w}
              height={hallBounds.h}
              cornerRadius={8}
              fill="#FFFFFF"
              stroke="#0A3D27"
              strokeWidth={2}
              shadowColor="rgba(10, 61, 39, 0.08)"
              shadowBlur={12}
            />

            {/* Grid Lines */}
            {gridLines.map((gl, i) => (
              <Line key={`grid-${i}`} points={gl.points} stroke={gl.stroke} strokeWidth={gl.strokeWidth} listening={false} />
            ))}

            {/* Hall Header Banner Indicator */}
            <Rect x={hallBounds.x + 10} y={hallBounds.y + 10} width={hallBounds.w - 20} height={26} fill="#F8FAFC" cornerRadius={4} />
            <Text
              x={hallBounds.x + 20}
              y={hallBounds.y + 18}
              text="PAVILION 3 • JIO WORLD CONVENTION CENTRE (JWCC), MUMBAI"
              fontSize={10}
              fontFamily="system-ui, sans-serif"
              fontStyle="bold"
              fill="#64748B"
              listening={false}
            />
          </Layer>

          {/* LAYER 2: Structural Pillars */}
          <Layer>
            {pillars.map((pil, idx) => (
              <Group key={`pillar-${idx}`}>
                <Circle cx={pil.cx} cy={pil.cy} radius={pil.r} fill="rgba(239, 68, 68, 0.2)" stroke="#EF4444" strokeWidth={1.5} />
                <Circle cx={pil.cx} cy={pil.cy} radius={2} fill="#EF4444" />
              </Group>
            ))}
          </Layer>

          {/* LAYER 3: Facilities & Non-Stall Layout Elements */}
          <Layer>
            {facilities.map((fac) => {
              const isSelected = selectedElement?.type === 'facility' && selectedElement.id === fac.id;

              let fillColor = '#E0F2FE';
              let strokeColor = '#0284C7';
              let textColor = '#0369A1';

              if (fac.type === 'cafeteria') {
                fillColor = '#FEF3C7';
                strokeColor = '#D97706';
                textColor = '#92400E';
              } else if (fac.type === 'conference') {
                fillColor = '#EEF2FF';
                strokeColor = '#4F46E5';
                textColor = '#3730A3';
              } else if (fac.type === 'registration') {
                fillColor = '#DCFCE7';
                strokeColor = '#16A34A';
                textColor = '#15803D';
              } else if (fac.type === 'organizer') {
                fillColor = '#FAF5FF';
                strokeColor = '#9333EA';
                textColor = '#6B21A8';
              }

              return (
                <Group
                  key={`facility-${fac.id}`}
                  x={fac.x}
                  y={fac.y}
                  draggable
                  onClick={() => onSelectElement('facility', fac.id)}
                  onTap={() => onSelectElement('facility', fac.id)}
                  onDragStart={(e) => {
                    dragStartPositions.current[fac.id] = { x: fac.x, y: fac.y };
                    onSelectElement('facility', fac.id);
                  }}
                  onDragMove={(e) => {
                    const rawX = e.target.x();
                    const rawY = e.target.y();
                    const snappedX = Math.round(rawX / snapGridSize) * snapGridSize;
                    const snappedY = Math.round(rawY / snapGridSize) * snapGridSize;
                    const clampedX = Math.max(hallBounds.x, Math.min(hallBounds.x + hallBounds.w - fac.w, snappedX));
                    const clampedY = Math.max(hallBounds.y, Math.min(hallBounds.y + hallBounds.h - fac.h, snappedY));
                    e.target.position({ x: clampedX, y: clampedY });
                  }}
                  onDragEnd={(e) => {
                    const finalX = e.target.x();
                    const finalY = e.target.y();
                    onUpdateFacilityPosition(fac.id, finalX, finalY);
                  }}
                >
                  <Rect
                    width={fac.w}
                    height={fac.h}
                    cornerRadius={6}
                    fill={fillColor}
                    stroke={isSelected ? '#0A3D27' : strokeColor}
                    strokeWidth={isSelected ? 2.5 : 1.2}
                    dash={isSelected ? [6, 3] : undefined}
                    shadowColor={isSelected ? 'rgba(10, 61, 39, 0.3)' : 'transparent'}
                    shadowBlur={isSelected ? 8 : 0}
                  />
                  <Text
                    x={0}
                    y={fac.h / 2 - 8}
                    width={fac.w}
                    align="center"
                    text={fac.title}
                    fontSize={Math.max(8, Math.min(13, fac.w / 8))}
                    fontFamily="system-ui, sans-serif"
                    fontStyle="bold"
                    fill={isSelected ? '#0A3D27' : textColor}
                    listening={false}
                  />
                  {fac.dimensions && (
                    <Text
                      x={0}
                      y={fac.h / 2 + 6}
                      width={fac.w}
                      align="center"
                      text={fac.dimensions}
                      fontSize={8}
                      fontFamily="monospace"
                      fill="#64748B"
                      listening={false}
                    />
                  )}
                </Group>
              );
            })}
          </Layer>

          {/* LAYER 4: Stalls Geometry (Draggable, Snappable, Overlap-Protected) */}
          <Layer>
            {stalls.map((stall) => {
              const isSelected = selectedElement?.type === 'stall' && selectedElement.id === stall.id;
              const isColliding = collidingId === stall.id;
              const liveBooth = boothsMap[stall.id];
              const effectiveStatus = liveBooth?.status || stall.initialStatus || 'available';

              // Visual styling mapped to stall category & live database status
              let fillColor = '#ECFDF5';
              let strokeColor = '#059669';
              let textColor = '#065F46';

              if (effectiveStatus === 'booked') {
                fillColor = '#F1F5F9';
                strokeColor = '#64748B';
                textColor = '#334155';
              } else if (effectiveStatus === 'on_hold') {
                fillColor = '#FFFBEB';
                strokeColor = '#D97706';
                textColor = '#92400E';
              } else if (stall.category === 'premium_12') {
                fillColor = '#FEF3C7';
                strokeColor = '#D97706';
                textColor = '#92400E';
              } else if (stall.category === 'grand_18') {
                fillColor = '#F5F3FF';
                strokeColor = '#7C3AED';
                textColor = '#5B21B6';
              } else if (stall.category === 'island_36') {
                fillColor = '#FFFBEB';
                strokeColor = '#B45309';
                textColor = '#78350F';
              } else if (stall.category === 'trading_table') {
                fillColor = '#F0FDF4';
                strokeColor = '#16A34A';
                textColor = '#166534';
              }

              // Collision highlight overrides
              if (isColliding) {
                fillColor = '#FEE2E2';
                strokeColor = '#EF4444';
                textColor = '#991B1B';
              } else if (isSelected) {
                fillColor = '#D1FAE5';
                strokeColor = '#0A3D27';
                textColor = '#0A3D27';
              }

              return (
                <Group
                  key={`stall-${stall.id}`}
                  x={stall.x}
                  y={stall.y}
                  draggable
                  onClick={() => onSelectElement('stall', stall.id)}
                  onTap={() => onSelectElement('stall', stall.id)}
                  onDragStart={(e) => {
                    dragStartPositions.current[stall.id] = { x: stall.x, y: stall.y };
                    onSelectElement('stall', stall.id);
                  }}
                  onDragMove={(e) => {
                    const rawX = e.target.x();
                    const rawY = e.target.y();

                    // Snap to grid
                    const snappedX = Math.round(rawX / snapGridSize) * snapGridSize;
                    const snappedY = Math.round(rawY / snapGridSize) * snapGridSize;

                    // Clamped within hall boundary
                    const clampedX = Math.max(hallBounds.x, Math.min(hallBounds.x + hallBounds.w - stall.w, snappedX));
                    const clampedY = Math.max(hallBounds.y, Math.min(hallBounds.y + hallBounds.h - stall.h, snappedY));

                    e.target.position({ x: clampedX, y: clampedY });

                    // Overlap detection during active dragging
                    if (preventOverlap) {
                      const collidedWith = checkCollisionWithOtherStalls(stall.id, {
                        x: clampedX,
                        y: clampedY,
                        w: stall.w,
                        h: stall.h,
                      });
                      if (collidedWith) {
                        setCollidingId(stall.id);
                      } else if (collidingId === stall.id) {
                        setCollidingId(null);
                      }
                    }
                  }}
                  onDragEnd={(e) => {
                    const finalX = e.target.x();
                    const finalY = e.target.y();

                    // Check for overlap at drop position
                    if (preventOverlap) {
                      const collidedWith = checkCollisionWithOtherStalls(stall.id, {
                        x: finalX,
                        y: finalY,
                        w: stall.w,
                        h: stall.h,
                      });

                      if (collidedWith) {
                        // REVERT to pre-drag position to strictly prevent overlap
                        const originalPos = dragStartPositions.current[stall.id] || { x: stall.x, y: stall.y };
                        e.target.position(originalPos);
                        setCollidingId(null);
                        if (onOverlapRejection) {
                          onOverlapRejection(stall.id, collidedWith);
                        }
                        return;
                      }
                    }

                    setCollidingId(null);
                    onUpdateStallPosition(stall.id, finalX, finalY);
                  }}
                >
                  {/* Stall Base Rect */}
                  <Rect
                    width={stall.w}
                    height={stall.h}
                    cornerRadius={2.5}
                    fill={fillColor}
                    stroke={isColliding ? '#EF4444' : isSelected ? '#0A3D27' : strokeColor}
                    strokeWidth={isColliding ? 2.5 : isSelected ? 2.5 : 1}
                    shadowColor={
                      isColliding
                        ? 'rgba(239, 68, 68, 0.4)'
                        : isSelected
                        ? 'rgba(10, 61, 39, 0.4)'
                        : 'rgba(0,0,0,0.04)'
                    }
                    shadowBlur={isColliding ? 10 : isSelected ? 8 : 2}
                  />

                  {/* Stall ID */}
                  <Text
                    x={0}
                    y={stall.h > 24 ? stall.h / 2 - 6 : stall.h / 2 - 4}
                    width={stall.w}
                    align="center"
                    text={stall.id}
                    fontSize={Math.max(6.5, Math.min(10, stall.w / 3.4))}
                    fontFamily="system-ui, sans-serif"
                    fontStyle="900"
                    fill={isColliding ? '#B91C1C' : isSelected ? '#0A3D27' : textColor}
                    listening={false}
                  />

                  {/* Secondary Label (dimensions or booked status) if space permits */}
                  {stall.h >= 28 && (
                    <Text
                      x={0}
                      y={stall.h / 2 + 5}
                      width={stall.w}
                      align="center"
                      text={effectiveStatus === 'booked' ? 'BOOKED' : stall.dimensions}
                      fontSize={6}
                      fontFamily="monospace"
                      fontStyle={effectiveStatus === 'booked' ? 'bold' : 'normal'}
                      fill={effectiveStatus === 'booked' ? '#64748B' : '#94A3B8'}
                      listening={false}
                    />
                  )}

                  {/* Live Database Status Dot Indicator */}
                  <Circle
                    x={stall.w - 4}
                    y={4}
                    radius={2}
                    fill={
                      effectiveStatus === 'booked'
                        ? '#64748B'
                        : effectiveStatus === 'on_hold'
                        ? '#F59E0B'
                        : '#10B981'
                    }
                  />
                </Group>
              );
            })}
          </Layer>
        </Stage>
      </div>
    </div>
  );
}
export default KonvaCanvas;
